import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTicketAlt, FaPlus, FaFilter, FaSearch, FaEye, FaEdit, FaTrash, FaClock, FaUser, FaTag, FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaBug, FaQuestionCircle, FaLightbulb, FaPaperPlane, FaGraduationCap, FaBookOpen, FaSync } from 'react-icons/fa';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import GlobalStyles from '../styles/GlobalStyles';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';

const palette = {
  background: '#eef2ff',
  panel: '#ffffff',
  surface: '#f8f9ff',
  border: 'rgba(47, 63, 236, 0.12)',
  primary: '#2f3fec',
  accent: '#0fc8ff',
  highlight: '#00c896',
  heading: '#1f2a44',
  subtleText: '#6b728c',
  neutral: '#9aa0b8',
  text: '#2b2f43',
  gradientPrimary: 'linear-gradient(135deg, #2f3fec 0%, #3f6ef9 100%)',
  gradientSecondary: 'linear-gradient(135deg, #00c4ff 0%, #4f46e5 100%)',
  danger: '#ef4444',
  warning: '#f59e0b'
};

// Styled Components
const TicketsContainer = styled.div`
  height: calc(100vh - 80px);
  width: 100%;
  padding: 0;
  position: relative;
  overflow: hidden;
  background: ${palette.background};
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  background: ${palette.panel};
  overflow: hidden;
`;

const MainLayout = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  flex: 1;
`;

const LeftSidebar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'showConversation'
})`
  width: 380px;
  background: ${palette.panel};
  border-right: 1px solid ${palette.border};
  box-shadow: 10px 0 30px rgba(47, 63, 236, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 100%;
    display: ${props => props.showConversation ? 'none' : 'flex'};
  }
`;

const ConversationPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'show'
})`
  flex: 1;
  background: linear-gradient(180deg, #fdfbff 0%, #eef6ff 40%, #f3fbff 100%);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  
  @media (max-width: 768px) {
    display: ${props => props.show ? 'flex' : 'none'};
  }
`;

const EmptyConversation = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: ${palette.primary};
  background: transparent;
  
  .empty-icon {
    font-size: 4rem;
    margin-bottom: 20px;
    opacity: 0.4;
  }
  
  h3 {
    margin-bottom: 10px;
    color: ${palette.heading};
    opacity: 0.85;
  }
  
  p {
    color: ${palette.subtleText};
    opacity: 0.8;
  }
`;

const PageTitle = styled.h1`
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
  padding: 18px 28px 14px 28px;
  background: ${palette.gradientPrimary};
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const ConversationHeader = styled.div`
  background: ${palette.surface};
  padding: 16px 24px;
  border-bottom: 1px solid ${palette.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  
  .ticket-info {
    display: flex;
    align-items: center;
    gap: 15px;
  }
  
  .ticket-title {
    font-weight: 600;
    color: ${palette.heading};
    font-size: 1rem;
  }
  
  .ticket-id {
    font-size: 0.8rem;
    color: ${palette.primary};
    background: rgba(47, 63, 236, 0.1);
    padding: 4px 8px;
    border-radius: 8px;
  }
  
  .back-button {
    display: none;
    
    @media (max-width: 768px) {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 35px;
      height: 35px;
      border-radius: 50%;
      background: rgba(47, 63, 236, 0.1);
      color: ${palette.primary};
      border: none;
      cursor: pointer;
      
      &:hover {
        background: rgba(47, 63, 236, 0.2);
      }
    }
  }
`;

const ControlsSection = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${palette.border};
  background: ${palette.panel};
  flex-shrink: 0;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 12px;
  background: rgba(47, 63, 236, 0.08);
  border-radius: 10px;
  padding: 4px;
`;

const FilterTab = styled.button`
  flex: 1;
  padding: 8px 12px;
  border: none;
  background: ${props => props.active ? palette.gradientPrimary : 'transparent'};
  color: ${props => props.active ? '#fff' : palette.subtleText};
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? palette.gradientPrimary : 'rgba(47, 63, 236, 0.15)'};
    color: ${props => props.active ? '#fff' : palette.primary};
  }
`;

const SearchAndFilter = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const RefreshButton = styled.button`
  padding: 8px 12px;
  background: ${palette.panel};
  border: 1px solid ${palette.border};
  border-radius: 8px;
  color: ${palette.primary};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: rgba(47, 63, 236, 0.08);
    border-color: ${palette.primary};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 0.9rem;
    transition: transform 0.3s ease;
  }
  
  &.refreshing svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const CreateTicketButton = styled.button`
  width: 100%;
  padding: 12px 18px;
  background: ${palette.gradientSecondary};
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
  box-shadow: 0 12px 20px rgba(0, 200, 255, 0.2);

  &:hover {
    filter: brightness(1.05);
  }
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;

  input {
    width: 100%;
    padding: 10px 12px 10px 36px;
    border: 1px solid ${palette.border};
    border-radius: 10px;
    font-size: 0.85rem;
    background: ${palette.surface};
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${palette.primary};
      background: ${palette.panel};
      box-shadow: 0 4px 12px rgba(47, 63, 236, 0.12);
    }
  }

  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${palette.neutral};
    font-size: 0.85rem;
  }
`;

const TicketsList = styled.div`
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
    
    &:hover {
      background: #999;
    }
  }
`;

const TicketItem = styled.div`
  padding: 16px 22px;
  border-bottom: 1px solid ${palette.border};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? '#e8edff' : palette.panel};
  border-left: 4px solid ${props => props.selected ? palette.primary : 'transparent'};
  position: relative;
  
  &:hover {
    background: ${props => props.selected ? '#e3e9ff' : palette.surface};
  }
`;

const TicketItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
  
  .ticket-id {
    font-size: 0.75rem;
    color: ${palette.primary};
    font-weight: 600;
  }
  
  .timestamp {
    font-size: 0.75rem;
    color: ${palette.neutral};
  }
`;

const TicketItemTitle = styled.h6`
  margin: 0 0 4px 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${palette.heading};
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TicketItemPreview = styled.p`
  margin: 0 0 6px 0;
  font-size: 0.8rem;
  color: ${palette.subtleText};
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TicketItemMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .status-priority {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .category {
    font-size: 0.75rem;
    color: ${palette.neutral};
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const CourseInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  color: ${palette.primary};
  background: rgba(47, 63, 236, 0.12);
  padding: 3px 8px;
  border-radius: 6px;
  margin-top: 6px;
  
  .course-icon {
    font-size: 0.6rem;
  }
  
  .course-text {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: ${palette.subtleText};
  margin-top: 4px;
  
  .student-avatar {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${palette.gradientPrimary};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.6rem;
    font-weight: 600;
    flex-shrink: 0;
  }
  
  .student-name {
    font-weight: 600;
    color: ${palette.heading};
  }
  
  .registration-number {
    color: ${palette.neutral};
    font-size: 0.7rem;
  }
`;

const ContextInfo = styled.div`
  background: rgba(47, 63, 236, 0.06);
  padding: 16px 18px;
  border-radius: 12px;
  margin-bottom: 16px;
  border-left: 4px solid ${palette.primary};
  
  .context-title {
    font-size: 0.8rem;
    font-weight: 600;
    color: ${palette.primary};
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .context-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    font-size: 0.75rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
  
  .context-item {
    display: flex;
    align-items: center;
    gap: 6px;
    color: ${palette.subtleText};
    
    .label {
      font-weight: 600;
      color: ${palette.heading};
    }
    
    .value {
      color: ${palette.primary};
    }
  }
`;

const StatusBadge = styled.div`
  padding: 3px 6px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.7rem;
  background: ${props => getStatusBackground(props.status)};
  color: ${props => getStatusTextColor(props.status)};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(21, 24, 77, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  z-index: 1000;
  backdrop-filter: blur(6px);
  animation: overlayFade 0.2s ease forwards;

  @keyframes overlayFade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 600px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.96), rgba(237, 241, 255, 0.9));
  border: 1px solid rgba(47, 63, 236, 0.15);
  border-radius: 20px;
  box-shadow: 0 20px 45px rgba(21, 24, 77, 0.15);
  position: relative;
  overflow: hidden;
  animation: modalCardIn 0.3s ease forwards;

  @keyframes modalCardIn {
    from {
      opacity: 0;
      transform: translateY(-10px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  border-bottom: 1px solid rgba(47, 63, 236, 0.15);
  padding: 25px 30px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const ModalTitle = styled.h4`
  color: ${palette.heading};
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${palette.subtleText};
  cursor: pointer;
  line-height: 1;
  padding: 0;
  transition: color 0.2s ease;

  &:hover {
    color: ${palette.danger};
  }
`;

const ModalBody = styled.div`
  padding: 25px 30px;
  max-height: calc(80vh - 200px);
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  border-top: 1px solid rgba(47, 63, 236, 0.15);
  padding: 20px 30px 25px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: ${palette.heading};
    font-size: 0.95rem;
  }

  input, select, textarea {
    width: 100%;
    padding: 12px 15px;
    border: 2px solid rgba(47, 63, 236, 0.15);
    border-radius: 12px;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    background: rgba(255, 255, 255, 0.9);

    &:focus {
      outline: none;
      border-color: ${palette.primary};
      box-shadow: 0 0 0 3px rgba(47, 63, 236, 0.15);
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 12px;
  font-weight: 600;
  border: none;
  transition: all 0.3s ease;
  color: #fff;
  cursor: pointer;
  background: ${props => props.variant === 'primary' ? palette.gradientPrimary : palette.neutral};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
    background: ${props => props.variant === 'primary' ? palette.gradientPrimary : '#7c8aa8'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ConversationContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background: linear-gradient(180deg, #f2f6ff 0%, #fdfcff 50%, #f2fbff 100%);
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(47, 63, 236, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(0, 194, 255, 0.06) 0%, transparent 50%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232f3fec' fill-opacity='0.05'%3E%3Cpath d='M10 10h10v2H10zm30 30h10v2H40zM60 0h10v2H60zM0 60h10v2H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
    pointer-events: none;
    z-index: 0;
  }
  
  .conversation-content {
    position: relative;
    z-index: 1;
    padding: 15px 12px;
    min-height: 100%;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const ConversationMessage = styled.div`
  display: flex;
  margin-bottom: 10px;
  align-items: flex-end;
  
  &.admin-message {
    justify-content: flex-start;
    
    .message-bubble {
      background: #ffffff;
      color: ${palette.text};
      border-bottom-left-radius: 3px;
      box-shadow: 0 10px 25px rgba(33, 43, 79, 0.15);
      position: relative;
      border: 1px solid rgba(47, 63, 236, 0.08);
      
      &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: -8px;
        width: 0;
        height: 0;
        border: 8px solid transparent;
        border-right: 8px solid #ffffff;
        border-bottom: 0;
        border-left: 0;
      }
    }
    
    .avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: ${palette.gradientPrimary};
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 14px;
      font-weight: 600;
      margin-right: 8px;
      margin-bottom: 2px;
      flex-shrink: 0;
    }
  }
  
  &.student-message {
    justify-content: flex-end;
    
    .message-bubble {
      background: rgba(0, 194, 255, 0.15);
      color: ${palette.text};
      border-bottom-right-radius: 3px;
      box-shadow: 0 10px 25px rgba(33, 43, 79, 0.15);
      position: relative;
      border: 1px solid rgba(0, 194, 255, 0.25);
      
      &::before {
        content: '';
        position: absolute;
        bottom: 0;
        right: -8px;
        width: 0;
        height: 0;
        border: 8px solid transparent;
        border-left: 8px solid rgba(0, 194, 255, 0.15);
        border-bottom: 0;
        border-right: 0;
      }
    }
  }
`;

const MessageBubble = styled.div`
  max-width: 65%;
  min-width: 100px;
  padding: 8px 12px 6px 12px;
  border-radius: 7.5px;
  position: relative;
  word-wrap: break-word;
  
  .message-text {
    margin: 0;
    line-height: 1.4;
    font-size: 14px;
    color: inherit;
    margin-bottom: 4px;
    white-space: pre-wrap;
    word-wrap: break-word;
    
    strong {
      font-weight: 700;
      color: inherit;
      text-shadow: 0.5px 0.5px 0px rgba(0,0,0,0.1);
      letter-spacing: 0.2px;
    }
  }
  
  .message-meta {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: rgba(31, 42, 68, 0.55);
    margin-top: 2px;
    
    .sender-name {
      font-weight: 600;
      color: ${palette.primary};
      margin-right: 6px;
      font-size: 12px;
    }
    
    .timestamp {
      font-size: 11px;
      color: rgba(31, 42, 68, 0.45);
    }
    
    .message-status {
      margin-left: 4px;
      font-size: 16px;
      color: ${palette.accent};
      
      &.delivered {
        color: ${palette.neutral};
      }
      
      &.read {
        color: ${palette.accent};
      }
    }
  }
`;

const DateSeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  
  .date-badge {
    background: ${palette.panel};
    color: ${palette.primary};
    padding: 6px 14px;
    border-radius: 14px;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 6px 15px rgba(31, 42, 68, 0.12);
  }
`;

const ReplySection = styled.div`
  background: ${palette.surface};
  border-top: 1px solid ${palette.border};
  overflow: hidden;
  
  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
  
  .reply-input-container {
    padding: 0;
    background: ${palette.panel};
    display: flex;
    align-items: flex-end;
    gap: 0;
  }
`;

const ReplyTextarea = styled.textarea`
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 14px 18px;
  border: none;
  resize: none;
  font-size: 13px;
  line-height: 1.4;
  font-family: inherit;
  background: transparent;
  color: ${palette.text};
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: ${palette.neutral};
  }
`;

const ReplyActions = styled.div`
  display: flex;
  align-items: flex-end;
  padding: 8px 12px;
  
  .send-button {
    background: ${palette.gradientSecondary};
    border: none;
    border-radius: 50%;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 10px 20px rgba(0, 200, 255, 0.35);
    
    &:hover:not(:disabled) {
      transform: scale(1.07);
    }
    
    &:disabled {
      background: ${palette.neutral};
      box-shadow: none;
      cursor: not-allowed;
      transform: none;
    }
    
    svg {
      font-size: 15px;
    }
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(47, 63, 236, 0.08);
  font-size: 12px;
  color: ${palette.primary};
  font-style: italic;
  
  .typing-dots {
    display: flex;
    gap: 2px;
    
    span {
      width: 4px;
      height: 4px;
      background: ${palette.primary};
      border-radius: 50%;
      animation: typingDots 1.5s infinite;
      
      &:nth-child(2) {
        animation-delay: 0.3s;
      }
      
      &:nth-child(3) {
        animation-delay: 0.6s;
      }
    }
  }
  
  @keyframes typingDots {
    0%, 60%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    30% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const OnlineStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: rgba(0, 200, 150, 0.15);
  color: ${palette.highlight};
  font-size: 12px;
  font-weight: 600;
  
  .status-dot {
    width: 8px;
    height: 8px;
    background: ${palette.highlight};
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const DisabledMessage = styled.div`
  text-align: center;
  padding: 20px;
  background: rgba(148, 163, 184, 0.15);
  border-radius: 12px;
  color: ${palette.subtleText};
  font-style: italic;
`;

// Helper functions for styling
const getStatusColor = (status) => {
  switch (status) {
    case 'open': return '#2563eb';
    case 'in-progress': return '#f97316';
    case 'resolved': return '#10b981';
    case 'closed': return '#94a3b8';
    default: return palette.primary;
  }
};

const getStatusGradient = (status) => {
  switch (status) {
    case 'open': return 'linear-gradient(90deg, #2563eb, #1d4ed8)';
    case 'in-progress': return 'linear-gradient(90deg, #f97316, #ea580c)';
    case 'resolved': return 'linear-gradient(90deg, #10b981, #059669)';
    case 'closed': return 'linear-gradient(90deg, #94a3b8, #64748b)';
    default: return palette.gradientPrimary;
  }
};

const getStatusBackground = (status) => {
  switch (status) {
    case 'open': return 'rgba(37, 99, 235, 0.18)';
    case 'in-progress': return 'rgba(249, 115, 22, 0.18)';
    case 'resolved': return 'rgba(16, 185, 129, 0.18)';
    case 'closed': return 'rgba(148, 163, 184, 0.2)';
    default: return 'rgba(47, 63, 236, 0.18)';
  }
};

const getStatusTextColor = (status) => {
  switch (status) {
    case 'open': return '#1d4ed8';
    case 'in-progress': return '#c2410c';
    case 'resolved': return '#0f766e';
    case 'closed': return '#475569';
    default: return palette.primary;
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'open': return <FaExclamationTriangle />;
    case 'in-progress': return <FaClock />;
    case 'resolved': return <FaCheckCircle />;
    case 'closed': return <FaTimesCircle />;
    default: return <FaTicketAlt />;
  }
};

const getPriorityIcon = (priority) => {
  switch (priority) {
    case 'high': return <FaExclamationTriangle style={{ color: palette.danger }} />;
    case 'medium': return <FaExclamationTriangle style={{ color: palette.warning }} />;
    case 'low': return <FaExclamationTriangle style={{ color: palette.highlight }} />;
    default: return <FaTag />;
  }
};

// Helper to convert category codes/slugs into readable labels
const formatCategoryLabel = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Function to format message text with bold support and line breaks
const formatMessageText = (text) => {
  if (!text) return '';
  
  // Split by line breaks first
  const lines = text.split('\n');
  
  return lines.map((line, lineIndex) => {
    // Split each line by both ** and * for bold formatting
    // First handle ** then handle single *
    const parts = line.split(/(\*\*.*?\*\*|\*.*?\*)/g);
    
    return (
      <span key={lineIndex}>
        {parts.map((part, partIndex) => {
          // Handle double asterisk **text**
          if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
            const boldText = part.slice(2, -2);
            return <strong key={partIndex}>{boldText}</strong>;
          }
          // Handle single asterisk *text* (but not if it's part of **)
          else if (part.startsWith('*') && part.endsWith('*') && part.length > 2 && !part.startsWith('**')) {
            const boldText = part.slice(1, -1);
            return <strong key={partIndex}>{boldText}</strong>;
          }
          return part;
        })}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    );
  });
};

const LoadingIndicator = ({ message = 'Loading...' }) => (
  <Loader message={message} />
);



const Tickets = () => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [courseInfo, setCourseInfo] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, unread, open, in-progress, resolved, closed
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showConversation, setShowConversation] = useState(false); // For mobile responsive
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  category: '',
  registrationNumber: '',
    priority: 'medium'
  });
  const [replyText, setReplyText] = useState('');
  const [error, setError] = useState(null);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const isParentUser = userInfo?.type === 'parent';
  const isStudentOrParentUser = userInfo?.type === 'student' || userInfo?.type === 'parent';

  useEffect(() => {
    initializeUserData();
    loadCategories();
    loadTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchTerm, filterType]);

  const initializeUserData = () => {
    try {
      const course = AuthService.getCourseInfo();
      const user = AuthService.getUserInfo();
      setCourseInfo(course);
      setUserInfo(user);
      // console.log('Course Info:', course);
      // console.log('User Info:', user);
      // Prefill registration number for student/parent from selected course context when available
      if (user?.type === 'student' || user?.type === 'parent') {
        const regNum = course?.registrationNumber || user?.registrationNumber || '';
        if (regNum) {
          setFormData(prev => ({ ...prev, registrationNumber: regNum }));
        }
      }
    } catch (error) {
      console.error('Error initializing user data:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get('/tickets/categories');
      setCategories(response.data);
      // Set default category to first available category
      if (response.data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: response.data[0].name }));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Failed to load categories');
    }
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      // Attempt to use up-to-date user/course info; fall back to AuthService if state not populated yet
      const authUser = userInfo || AuthService.getUserInfo();
      const authCourse = courseInfo || AuthService.getCourseInfo();

      const params = {
        filter: filterType,
        search: searchTerm,
        page: 1,
        limit: 50
      };

      // For student/parent users include selected registrationNumber to scope tickets by selected batch context
      if (authUser?.type === 'student' || authUser?.type === 'parent') {
        const regNumber = formData.registrationNumber || authCourse?.registrationNumber || authUser?.registrationNumber || '';
        if (regNumber) {
          params.registrationNumber = regNumber;
        }
      }

      const response = await axiosInstance.get('/tickets', { params });
      setTickets(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading tickets:', error);
      setError('Failed to load tickets');
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      // Build params similar to loadTickets; include registrationNumber for students
      const authUser = userInfo || AuthService.getUserInfo();
      const authCourse = courseInfo || AuthService.getCourseInfo();

      const params = {
        filter: filterType,
        search: searchTerm,
        page: 1,
        limit: 50
      };

      if (authUser?.type === 'student' || authUser?.type === 'parent') {
        const regNumber = formData.registrationNumber || authCourse?.registrationNumber || authUser?.registrationNumber || '';
        if (regNumber) params.registrationNumber = regNumber;
      }

      const response = await axiosInstance.get('/tickets', { params });
      setTickets(response.data);
      
      // If a ticket is currently selected, refresh its details too
      if (selectedTicket) {
        try {
          const ticketResponse = await axiosInstance.get(`/tickets/${selectedTicket.id}`);
          setSelectedTicket(ticketResponse.data);
        } catch (ticketError) {
          console.error('Error refreshing selected ticket:', ticketError);
        }
      }
      
    } catch (error) {
      console.error('Error refreshing tickets:', error);
      setError('Failed to refresh tickets');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchTicketConversation = async (ticketId) => {
    try {
      const response = await axiosInstance.get(`/tickets/${ticketId}/conversation`);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation:', error);
      throw error;
    }
  };

  const filterTickets = () => {
    let filtered = tickets;

    if (searchTerm) {
      filtered = filtered.filter(ticket =>
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ticket.course?.name && ticket.course.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (ticket.student?.name && ticket.student.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (ticket.student?.registrationNumber && ticket.student.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    switch (filterType) {
      case 'open':
        filtered = filtered.filter(ticket => ticket.status === 'open');
        break;
      case 'in-progress':
        filtered = filtered.filter(ticket => ticket.status === 'in-progress');
        break;
      case 'resolved':
        filtered = filtered.filter(ticket => ticket.status === 'resolved');
        break;
      case 'closed':
        filtered = filtered.filter(ticket => ticket.status === 'closed');
        break;
      default:
        // 'all' - no additional filtering
        break;
    }

    setFilteredTickets(filtered);
  };

  const handleCreateTicket = async () => {
    try {
      setError(null);

      if (isParentUser) {
        setError('Parent users cannot create tickets.');
        return;
      }
      
      if (!formData.title || !formData.description || !formData.category) {
        setError('Please fill in all required fields');
        return;
      }

      // For student/parent users derive registrationNumber from selected course context (do not expose/edit in UI)
      let registrationNumberValue = null;
      if (isStudentOrParentUser) {
        registrationNumberValue = courseInfo?.registrationNumber || userInfo?.registrationNumber || formData.registrationNumber || '';
        if (!registrationNumberValue) {
          setError('Registration number is required for the selected batch.');
          return;
        }
      }

      // Include course context if available
      const ticketData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        priority: formData.priority || 'medium',
          // Include registrationNumber for student/parent to keep tickets scoped by selected batch
        ...(isStudentOrParentUser && { registrationNumber: registrationNumberValue }),
        // Include course information for better context
        courseContext: courseInfo ? {
          courseId: courseInfo.courseId,
          courseName: courseInfo.courseName,
          batchId: courseInfo.batchId,
          batchName: courseInfo.batchName
        } : null
      };

      const response = await axiosInstance.post('/tickets', ticketData);
      
      // Add the new ticket to the list
      setTickets([response.data, ...tickets]);
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        category: categories.length > 0 ? categories[0].name : '',
        registrationNumber: '',
        priority: 'medium'
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
      setError(error.response?.data?.message || 'Failed to create ticket');
    }
  };

  const handleViewTicket = async (ticket) => {
    try {
      setLoadingTicket(true);
      setError(null);
      const response = await axiosInstance.get(`/tickets/${ticket.id}`);
      setSelectedTicket(response.data);
      setShowConversation(true); // For mobile
      setReplyText(''); // Reset reply text when opening ticket
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setError('Failed to load ticket details');
    } finally {
      setLoadingTicket(false);
    }
  };

  const handleBackToList = () => {
    setSelectedTicket(null);
    setShowConversation(false);
  };

  const handleDeleteTicket = async (ticketId) => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      try {
        setError(null);
        await axiosInstance.delete(`/tickets/${ticketId}`);
        setTickets(tickets.filter(ticket => ticket.id !== ticketId));
        
        // If the deleted ticket was selected, clear selection
        if (selectedTicket && selectedTicket.id === ticketId) {
          setSelectedTicket(null);
          setShowConversation(false);
        }
      } catch (error) {
        console.error('Error deleting ticket:', error);
        setError(error.response?.data?.message || 'Failed to delete ticket');
      }
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;

    try {
      if (isParentUser) {
        setError('Parent users cannot reply in ticket chat.');
        return;
      }

      setSendingReply(true);
      setError(null);
      const response = await axiosInstance.post(`/tickets/${selectedTicket.id}/reply`, { 
        message: replyText 
      });
      
      // Add the new message to the conversation
      const updatedConversation = [...selectedTicket.conversation, response.data];
      
      // Update selected ticket
      const updatedSelectedTicket = {
        ...selectedTicket,
        conversation: updatedConversation,
        updatedAt: new Date().toISOString()
      };
      
      setSelectedTicket(updatedSelectedTicket);
      
      // Update the ticket in the tickets list
      const updatedTickets = tickets.map(ticket => {
        if (ticket.id === selectedTicket.id) {
          return {
            ...ticket,
            lastMessage: replyText,
            updatedAt: new Date().toISOString()
          };
        }
        return ticket;
      });
      
      setTickets(updatedTickets);
      setReplyText('');
    } catch (error) {
      console.error('Error sending reply:', error);
      setError(error.response?.data?.message || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const canEditOrDelete = (status) => {
    return status === 'open';
  };

  const canReply = (status) => {
    if (isParentUser) {
      return false;
    }
    return status !== 'resolved' && status !== 'closed';
  };

  const getLastMessagePreview = (ticket) => {
    if (ticket.lastMessage) {
      return ticket.lastMessage;
    }
    return ticket.description;
  };

  return (
    <StudentLayout>
      <GlobalStyles />
      <TicketsContainer>
        {error && (
          <div style={{
            background: '#ffe4e9',
            color: palette.danger,
            padding: '12px 20px',
            borderLeft: `4px solid ${palette.danger}`,
            margin: '12px 20px',
            borderRadius: '8px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}
        {loading ? (
          <LoadingIndicator message="Loading your tickets..." />
        ) : (
          <MainLayout>
            {/* Left Sidebar - Tickets List */}
            <LeftSidebar showConversation={showConversation}>
              <PageTitle>
                <FaTicketAlt /> Support Tickets
                {courseInfo && (
                  <span style={{ 
                    fontSize: '0.8rem', 
                    fontWeight: '400', 
                    opacity: '0.8',
                    marginLeft: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <FaGraduationCap style={{ fontSize: '0.7rem' }} />
                    {courseInfo.courseName || courseInfo.courseFullName}
                  </span>
                )}
              </PageTitle>
              
              <ControlsSection>
                <FilterTabs>
                  <FilterTab 
                    active={filterType === 'all'} 
                    onClick={() => setFilterType('all')}
                  >
                    All
                  </FilterTab>
                  <FilterTab 
                    active={filterType === 'open'} 
                    onClick={() => setFilterType('open')}
                  >
                    Open
                  </FilterTab>
                  <FilterTab 
                    active={filterType === 'in-progress'} 
                    onClick={() => setFilterType('in-progress')}
                  >
                    In Progress
                  </FilterTab>
                  <FilterTab 
                    active={filterType === 'resolved'} 
                    onClick={() => setFilterType('resolved')}
                  >
                    Resolved
                  </FilterTab>
                  <FilterTab 
                    active={filterType === 'closed'} 
                    onClick={() => setFilterType('closed')}
                  >
                    Closed
                  </FilterTab>
                </FilterTabs>
                
                <SearchAndFilter>
                  <SearchBox>
                    <FaSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder={userInfo?.type === 'student' ? 
                        "Search tickets, courses..." : 
                        "Search tickets, students, courses..."
                      }
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </SearchBox>
                  <RefreshButton 
                    onClick={handleRefresh}
                    disabled={refreshing || loading}
                    className={refreshing ? 'refreshing' : ''}
                    title="Refresh tickets"
                  >
                    <FaSync />
                  </RefreshButton>
                </SearchAndFilter>

                {!isParentUser && (
                  <CreateTicketButton onClick={() => setShowCreateModal(true)}>
                    <FaPlus /> Submit New Ticket
                  </CreateTicketButton>
                )}
              </ControlsSection>

              <TicketsList>
                {filteredTickets.length === 0 ? (
                  <div style={{ padding: '30px 20px', textAlign: 'center', color: '#999' }}>
                    <FaTicketAlt style={{ fontSize: '1.5rem', marginBottom: '8px', opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: '0.85rem' }}>
                      {tickets.length === 0 
                        ? "No tickets yet. Create your first ticket to get started."
                        : "No tickets match your filters."
                      }
                    </p>
                  </div>
                ) : (
                  filteredTickets.map((ticket) => (
                    <TicketItem
                      key={ticket.id}
                      selected={selectedTicket?.id === ticket.id}
                      onClick={() => handleViewTicket(ticket)}
                    >
                      <TicketItemHeader>
                        <span className="ticket-id">{ticket.id}</span>
                        <span className="timestamp">
                          {new Date(ticket.updatedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </TicketItemHeader>
                      
                      <TicketItemTitle>{ticket.title}</TicketItemTitle>
                      <TicketItemPreview>{getLastMessagePreview(ticket)}</TicketItemPreview>
                      
                      <TicketItemMeta>
                        <div className="status-priority">
                          <StatusBadge status={ticket.status}>
                            {getStatusIcon(ticket.status)}
                            {ticket.status.replace('-', ' ')}
                          </StatusBadge>
                        </div>
                        <div className="category">
                          {formatCategoryLabel(ticket.category)}
                        </div>
                      </TicketItemMeta>
                      
                      {/* Course Information */}
                      {ticket.course?.name && (
                        <CourseInfo>
                          <FaGraduationCap className="course-icon" />
                          <span className="course-text" title={ticket.course.name}>
                            {ticket.course.name}
                          </span>
                        </CourseInfo>
                      )}
                      
                      {/* Student Information - Only show if user is staff/admin and ticket has student info */}
                      {(userInfo?.type === 'staff' || userInfo?.type === 'super_admin') && ticket.student?.name && (
                        <StudentInfo>
                          <div className="student-avatar">
                            {ticket.student.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="student-name">{ticket.student.name}</div>
                            {ticket.student.registrationNumber && (
                              <div className="registration-number">
                                {ticket.student.registrationNumber}
                              </div>
                            )}
                          </div>
                        </StudentInfo>
                      )}
                    </TicketItem>
                  ))
                )}
              </TicketsList>
            </LeftSidebar>

            {/* Right Panel - Conversation */}
            <ConversationPanel show={showConversation}>
              {loadingTicket ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  flexDirection: 'column',
                  gap: '15px',
                  color: palette.primary
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid #f3f3f3',
                    borderTop: `3px solid ${palette.primary}`,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>Loading conversation...</p>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              ) : selectedTicket ? (
                <>
                  <ConversationHeader>
                    <button className="back-button" onClick={handleBackToList}>
                      ←
                    </button>
                    <div className="ticket-info">
                      <div className="ticket-title">{selectedTicket.title}</div>
                      <div className="ticket-id">{selectedTicket.id}</div>
                    </div>
                    <StatusBadge status={selectedTicket.status}>
                      {getStatusIcon(selectedTicket.status)}
                      {selectedTicket.status.replace('-', ' ')}
                    </StatusBadge>
                  </ConversationHeader>

                  {/* Context Information Panel */}
                  {(selectedTicket.courseName || selectedTicket.batchName || selectedTicket.studentName) && (
                    <ContextInfo>
                      <div className="context-title">
                        <FaBookOpen />
                        Academic Context
                      </div>
                      <div className="context-details">
                        {selectedTicket.courseName && (
                          <div className="context-item">
                            <span className="label">Course:</span>
                            <span className="value">{selectedTicket.courseName}</span>
                          </div>
                        )}
                        {selectedTicket.batchName && (
                          <div className="context-item">
                            <span className="label">Batch:</span>
                            <span className="value">{selectedTicket.batchName}</span>
                          </div>
                        )}
                        {selectedTicket.studentName && (userInfo?.type === 'staff' || userInfo?.type === 'super_admin') && (
                          <div className="context-item">
                            <span className="label">Student:</span>
                            <span className="value">{selectedTicket.studentName}</span>
                          </div>
                        )}
                        {selectedTicket.registrationNumber && (userInfo?.type === 'staff' || userInfo?.type === 'super_admin') && (
                          <div className="context-item">
                            <span className="label">Reg. No:</span>
                            <span className="value">{selectedTicket.registrationNumber}</span>
                          </div>
                        )}
                      </div>
                    </ContextInfo>
                  )}

                  <ConversationContainer>
                    <div className="conversation-content">
                      {/* Initial ticket description */}
                      <ConversationMessage className="student-message">
                        <MessageBubble className="message-bubble">
                          <div className="message-text">{formatMessageText(selectedTicket.description)}</div>
                          <div className="message-meta">
                            <span className="timestamp">
                              {new Date(selectedTicket.createdAt).toLocaleTimeString('en-US', { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true 
                              })}
                            </span>
                          </div>
                        </MessageBubble>
                      </ConversationMessage>

                      {/* Conversation messages */}
                      {selectedTicket.conversation && selectedTicket.conversation.map((message, index) => {
                        const prevMessage = selectedTicket.conversation[index - 1];
                        const showDateSeparator = !prevMessage || 
                          new Date(message.timestamp).toDateString() !== new Date(prevMessage.timestamp).toDateString();
                        
                        return (
                          <div key={message.id}>
                            {showDateSeparator && (
                              <DateSeparator>
                                <div className="date-badge">
                                  {new Date(message.timestamp).toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}
                                </div>
                              </DateSeparator>
                            )}
                            <ConversationMessage className={`${message.sender}-message`}>
                              {message.sender === 'admin' && (
                                <div className="avatar">
                                  {message.senderName.charAt(0)}
                                </div>
                              )}
                              <MessageBubble className="message-bubble">
                                {message.sender === 'admin' && (
                                  <div className="message-meta">
                                    <span className="sender-name">{message.senderName}</span>
                                  </div>
                                )}
                                <div className="message-text">{formatMessageText(message.message)}</div>
                                <div className="message-meta">
                                  <span className="timestamp">
                                    {new Date(message.timestamp).toLocaleTimeString('en-US', { 
                                      hour: '2-digit', 
                                      minute: '2-digit',
                                      hour12: true 
                                    })}
                                  </span>
                                </div>
                              </MessageBubble>
                            </ConversationMessage>
                          </div>
                        );
                      })}

                      {/* Sending indicator */}
                      {sendingReply && (
                        <ConversationMessage className="student-message">
                          <MessageBubble className="message-bubble" style={{ opacity: 0.7, background: '#e8f5e8' }}>
                            <div className="message-text">{replyText}</div>
                            <div className="message-meta">
                              <span style={{ 
                                fontSize: '10px', 
                                color: '#999', 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '5px' 
                              }}>
                                <div style={{
                                  width: '10px',
                                  height: '10px',
                                  border: '2px solid #ddd',
                                  borderTop: `2px solid ${palette.primary}`,
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }}></div>
                                Sending...
                              </span>
                            </div>
                          </MessageBubble>
                        </ConversationMessage>
                      )}
                    </div>
                  </ConversationContainer>

                  {/* Reply Section */}
                  <ReplySection className={!canReply(selectedTicket.status) ? 'disabled' : ''}>
                    {canReply(selectedTicket.status) ? (
                      <div className="reply-input-container">
                        <ReplyTextarea
                          placeholder="Type a message..."
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (replyText.trim()) {
                                handleSendReply();
                              }
                            }
                          }}
                        />
                        <ReplyActions>
                          <button 
                            className="send-button"
                            onClick={handleSendReply}
                            disabled={!replyText.trim() || sendingReply}
                            title={sendingReply ? "Sending..." : "Send Message"}
                          >
                            {sendingReply ? (
                              <div style={{
                                width: '14px',
                                height: '14px',
                                border: '2px solid #ffffff40',
                                borderTop: '2px solid #ffffff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}></div>
                            ) : (
                              <FaPaperPlane />
                            )}
                          </button>
                        </ReplyActions>
                      </div>
                    ) : (
                      <DisabledMessage>
                        {isParentUser
                          ? 'Parent users cannot chat in tickets.'
                          : selectedTicket.status === 'resolved' || selectedTicket.status === 'closed' 
                          ? `🔒 This ticket has been ${selectedTicket.status} and no longer accepts replies.`
                          : '⏳ Chat is available for open and in-progress tickets.'
                        }
                      </DisabledMessage>
                    )}
                  </ReplySection>
                </>
              ) : (
                <EmptyConversation>
                  <FaTicketAlt className="empty-icon" />
                  <h3>Select a ticket</h3>
                  <p>Choose a ticket from the list to view the conversation</p>
                </EmptyConversation>
              )}
            </ConversationPanel>
          </MainLayout>
        )}

        {/* Create Ticket Modal */}
        {!isParentUser && showCreateModal && (
          <ModalOverlay onClick={handleCloseCreateModal}>
            <ModalCard
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-ticket-title"
              onClick={(event) => event.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle id="create-ticket-title">
                  <FaPlus /> Submit New Ticket
                </ModalTitle>
                <ModalCloseButton
                  type="button"
                  aria-label="Close create ticket modal"
                  onClick={handleCloseCreateModal}
                >
                  ×
                </ModalCloseButton>
              </ModalHeader>
              <ModalBody>
                {/* Course Context Display */}
                {courseInfo && (
                  <ContextInfo style={{ marginBottom: '20px' }}>
                    <div className="context-title">
                      <FaGraduationCap />
                      Current Course Context
                    </div>
                    <div className="context-details">
                      <div className="context-item">
                        <span className="label">Course:</span>
                        <span className="value">{courseInfo.courseName || courseInfo.courseFullName}</span>
                      </div>
                      {courseInfo.batchName && (
                        <div className="context-item">
                          <span className="label">Batch:</span>
                          <span className="value">{courseInfo.batchName}</span>
                        </div>
                      )}
                    </div>
                  </ContextInfo>
                )}

                <FormGroup>
                  <label>Title</label>
                  <input
                    type="text"
                    placeholder="Enter ticket title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Description</label>
                  <textarea
                    placeholder="Describe your issue or request"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.name}>
                        {formatCategoryLabel(category.name)}
                      </option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </FormGroup>

                {/* Registration number is handled automatically for student users; not editable in UI */}
              </ModalBody>
              <ModalFooter>
                <ModalButton
                  type="button"
                  variant="secondary"
                  onClick={handleCloseCreateModal}
                >
                  Cancel
                </ModalButton>
                <ModalButton
                  type="button"
                  variant="primary"
                  onClick={handleCreateTicket}
                  disabled={
                    !formData.title ||
                    !formData.description ||
                    (isStudentOrParentUser && !(courseInfo?.registrationNumber || userInfo?.registrationNumber || formData.registrationNumber))
                  }
                >
                  Create Ticket
                </ModalButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}
      </TicketsContainer>
    </StudentLayout>
  );
};

export default Tickets;
